import { useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";

const FORM_ADDR = "https://readdy.ai/api/form/db7ne573hiaolq192jl0";

export default function AboutPage() {
  useSeo({
    title: "关于我们与联系方式 - 蜜桃成熟时1997影视内容站",
    description:
      "了解蜜桃成熟时1997影视内容站的定位与内容范围，并通过留言反馈表单或邮箱与我们联系，分享你对经典电影的看法与建议。",
    keywords: "关于我们,联系方式,留言反馈,蜜桃成熟时1997,影视内容站",
  });

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("留言已提交，感谢你的反馈！");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setSubmitting(true);
    setStatus("idle");
    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";

      const isSpam = /spam|form data is spam/i.test(serverMsg);
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setMessage("留言已提交，感谢你的反馈！我们会尽快查看。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-8 md:pt-10">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
      </div>

      <section className="w-full px-4 md:px-8 pt-6 pb-10 md:pb-12">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">
          <i className="ri-information-line"></i>
          关于本站
        </span>
        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
          关于蜜桃成熟时1997影视内容站
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          我们是一个专注于经典电影介绍与剧情解析的内容站点，以《蜜桃成熟时1997》为核心，延伸到九十年代经典亚洲电影。
        </p>
      </section>

      <section className="w-full px-4 md:px-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-5">
            <article className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-focus-3-line text-primary-600"></i>
                网站定位
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-700 leading-loose">
                本站面向对经典电影感兴趣的影迷、影视研究者与普通观众，提供详尽的电影介绍、剧情解析、演员资料与观影指南。我们希望兼具权威的资料来源与温暖的表达方式，让阅读与研究都成为一件舒服的事。
              </p>
            </article>

            <article className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-stack-line text-primary-600"></i>
                内容介绍
              </h2>
              <ul className="mt-3 space-y-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                <li className="flex gap-3">
                  <i className="ri-check-line text-primary-600 mt-1"></i>
                  影片资料库：收录九十年代经典亚洲电影，提供导演、主演、类型、时长与上映日期等完整资料。
                </li>
                <li className="flex gap-3">
                  <i className="ri-check-line text-primary-600 mt-1"></i>
                  剧情解析专题：从时间结构、符号隐喻与视听语言等角度，提供深度解析文章。
                </li>
                <li className="flex gap-3">
                  <i className="ri-check-line text-primary-600 mt-1"></i>
                  演员介绍：整理主要演员的简介与代表作品，串联影片与人物。
                </li>
                <li className="flex gap-3">
                  <i className="ri-check-line text-primary-600 mt-1"></i>
                  观影指南：提供观影建议、影视史背景与最新资讯。
                </li>
              </ul>
            </article>

            <article className="rounded-lg border border-accent-200 bg-accent-50/60 p-6 md:p-8">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-error-warning-line text-accent-600"></i>
                功能范围说明
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-700 leading-loose">
                本站为内容展示型网站，仅提供影片介绍与解析。站内所有内容均为示范虚构数据，不含任何播放、下载与付费功能；收藏功能基于浏览器本地存储实现，仅作演示用途。
              </p>
            </article>
          </div>

          <div className="space-y-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-mail-send-line text-primary-600"></i>
                联系我们
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                如有内容纠错、合作意向或任何建议，欢迎通过以下方式联系我们。
              </p>
              <a
                href="mailto:hello@tao1997.example.com"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors"
              >
                <i className="ri-mail-line"></i>
                hello@tao1997.example.com
              </a>
              <div className="mt-5 pt-5 border-t border-background-200 space-y-2 text-sm text-foreground-600">
                <p className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  回复时间：工作日 24 小时内
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-map-pin-line"></i>
                  所在地：香港 · 内容整理小组
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-links-line text-primary-600"></i>
                快速入口
              </h2>
              <div className="mt-4 flex flex-col gap-2">
                <Link to="/category" className="text-sm text-foreground-700 hover:text-primary-600 flex items-center gap-2">
                  <i className="ri-film-line"></i> 电影分类
                </Link>
                <Link to="/features/plot-analysis" className="text-sm text-foreground-700 hover:text-primary-600 flex items-center gap-2">
                  <i className="ri-lightbulb-line"></i> 剧情解析专题
                </Link>
                <Link to="/guides" className="text-sm text-foreground-700 hover:text-primary-600 flex items-center gap-2">
                  <i className="ri-compass-3-line"></i> 观影指南
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-16">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
            留言反馈
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            留下你的想法与建议，我们会认真阅读每一条留言。
          </p>

          <form
            id="about-feedback-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            <div className="flex flex-col gap-1.5">
              <label htmlFor="fb-name" className="text-sm text-foreground-700">
                你的称呼
              </label>
              <input
                id="fb-name"
                name="name"
                type="text"
                required
                placeholder="请输入你的称呼"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="fb-email" className="text-sm text-foreground-700">
                邮箱
              </label>
              <input
                id="fb-email"
                name="email"
                type="email"
                required
                placeholder="请输入邮箱，方便我们回复"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <label htmlFor="fb-topic" className="text-sm text-foreground-700">
                反馈类型
              </label>
              <select
                id="fb-topic"
                name="topic"
                defaultValue="内容建议"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="内容建议">内容建议</option>
                <option value="内容纠错">内容纠错</option>
                <option value="合作咨询">合作咨询</option>
                <option value="其他">其他</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <label htmlFor="fb-message" className="text-sm text-foreground-700">
                留言内容
              </label>
              <textarea
                id="fb-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请输入你的留言（最多 500 字）"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-y"
              ></textarea>
            </div>

            <input
              className="feedback-note-input"
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                <i className="ri-send-plane-line"></i>
                {submitting ? "提交中..." : "提交留言"}
              </button>
              <p className="text-xs text-foreground-400">
                提交即表示同意我们仅将信息用于回复本次留言。
              </p>
            </div>

            {status !== "idle" && (
              <div
                className={`md:col-span-2 rounded-md px-4 py-3 text-sm ${
                  status === "success"
                    ? "bg-primary-100 text-primary-800"
                    : "bg-accent-100 text-accent-800"
                }`}
              >
                <i
                  className={
                    status === "success"
                      ? "ri-checkbox-circle-line mr-1.5"
                      : "ri-error-warning-line mr-1.5"
                  }
                ></i>
                {message}
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}